import { hasPassportSuperAdminRole } from '@frontend-platform/authz';
import { useSharedKey } from './use-shared-key';

// Returns null when passport isn't seeded yet (no identity, or the host hasn't
// published one). Treat that as "not a super admin": this widens access, so an
// unknown identity must fall through to the narrower creator check.
function getIsSuperAdminSnapshot(): boolean {
  return hasPassportSuperAdminRole() === true;
}

/**
 * Whether the current user holds the Super Admin **role**.
 *
 * Deliberately the role check (`roles[].userRoles[].roleId === 1`) rather than
 * the `hasAccess('studio/*\/*', 'SuperAdmin')` policy probe: this mirrors
 * aistudio's `useMetricPermissions()`, which compares
 * `store.state.userRole.roleId` against that same id. The policy probe answers
 * a different question (does a policy document grant an action named
 * `SuperAdmin` on that path) and could diverge from the host's own answer.
 *
 * No extra host-state field is needed: `createFederationApp`'s `syncPassport()`
 * already seeds `@frontend-platform/authz` from the published `passportUser`
 * on mount, and re-seeds on every `passportUser`/`bots` publish —
 * `passportUser.roles[].userRoles[]` is the structure aistudio itself walks in
 * `Init/index.vue`'s `updatePassportUser`.
 *
 * Reactive for the same reason `use-is-dynamic-env` is: the host's auth
 * bootstrap is async, so a one-time read can run before the first publish and
 * lock in `false` for the whole mount. Keyed on `passportUser` so a late
 * (or changed) identity re-seeds and re-renders instead of staying stale.
 */
export function useIsSuperAdmin(): boolean {
  return useSharedKey('passportUser', getIsSuperAdminSnapshot);
}
